import React, { useState, useEffect, useRef } from 'react';
import './AIAssistant.css';
import aiAssistantService from '../../services/aiAssistantService';

// Simple icon components
const Mic = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
    <line x1="12" y1="19" x2="12" y2="23"/>
    <line x1="8" y1="23" x2="16" y2="23"/>
  </svg>
);

const MicOff = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <line x1="1" y1="1" x2="23" y2="23"/>
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
    <line x1="12" y1="19" x2="12" y2="23"/>
    <line x1="8" y1="23" x2="16" y2="23"/>
  </svg>
);

const Send = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22,2 15,22 11,13 2,9 22,2"/>
  </svg>
);

const Volume2 = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <polygon points="11,5 6,9 2,9 2,15 6,15 11,19 11,5"/>
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
  </svg>
);

const VolumeX = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <polygon points="11,5 6,9 2,9 2,15 6,15 11,19 11,5"/>
    <line x1="23" y1="9" x2="17" y2="15"/>
    <line x1="17" y1="9" x2="23" y2="15"/>
  </svg>
);

const X = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const Minimize2 = ({ size = 20, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
    <polyline points="4,14 10,14 10,20"/>
    <polyline points="20,10 14,10 14,4"/>
    <line x1="14" y1="10" x2="21" y2="3"/>
    <line x1="3" y1="21" x2="10" y2="14"/>
  </svg>
);

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  audioUrl?: string;
  suggestions?: string[];
  intent?: string;
  entities?: any[];
}

interface AIAssistantProps {
  isOpen: boolean;
  onClose: () => void;
  onMinimize: () => void;
  userId: string;
  sessionId: string;
  language?: string;
}

const AIAssistant: React.FC<AIAssistantProps> = ({
  isOpen,
  onClose,
  onMinimize,
  userId,
  sessionId,
  language = 'en'
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(language);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [voiceMode, setVoiceMode] = useState(false);
  const [hasWelcomed, setHasWelcomed] = useState(false);
  const [isReadingProducts, setIsReadingProducts] = useState(false);
  const [productOffset, setProductOffset] = useState(0);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isReadingProductsRef = useRef(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (isOpen) {
      initializeAudioContext();
      addWelcomeMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, []);

  // Auto-start conversation when voice mode is activated
  useEffect(() => {
    if (voiceMode && !hasWelcomed) {
      console.log('Voice mode activated, sending welcome message');
      
      let welcomeMessage = "Hey there! I'm now in voice mode! 🎤 Just click the microphone and start talking - I'll speak back to you automatically! What would you like to chat about?";
      
      if (currentLanguage === 'te') {
        welcomeMessage = "హాయ్! నేను ఇప్పుడు వాయిస్ మోడ్‌లో ఉన్నాను! 🎤 మైక్రోఫోన్‌ను క్లిక్ చేసి మాట్లాడటం ప్రారంభించండి - నేను మీకు స్వయంచాలకంగా మాట్లాడతాను! మీరు దేని గురించి చాట్ చేయాలనుకుంటున్నారు?";
      }
      
      const assistantMessage: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: welcomeMessage,
        timestamp: new Date(),
        audioUrl: `${process.env.REACT_APP_AI_SERVICE_URL}/api/v1/voice/text-to-speech?text=${encodeURIComponent(welcomeMessage)}&language=${currentLanguage}`
      };

      setMessages(prev => [...prev, assistantMessage]);
      setHasWelcomed(true);
      
      // Auto-play the welcome message
      setTimeout(() => {
        playAudio(assistantMessage.audioUrl!);
      }, 500);
    } else if (!voiceMode) {
      setHasWelcomed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceMode, hasWelcomed, currentLanguage]);

  const initializeAudioContext = async () => {
    try {
      const context = new (window.AudioContext || (window as any).webkitAudioContext)();
      setAudioContext(context);
    } catch (error) {
      console.error('Failed to initialize audio context:', error);
    }
  };

  const addWelcomeMessage = () => {
    const welcomeMessages = {
      en: "Hello! I'm your AI assistant. How can I help you today?",
      es: "¡Hola! Soy tu asistente de IA. ¿Cómo puedo ayudarte hoy?",
      hi: "नमस्ते! मैं आपका AI सहायक हूं। आज मैं आपकी कैसे मदद कर सकता हूं?"
    };

    const welcomeMessage: Message = {
      id: Date.now().toString(),
      type: 'assistant',
      content: welcomeMessages[currentLanguage as keyof typeof welcomeMessages] || welcomeMessages.en,
      timestamp: new Date(),
      suggestions: ['Browse products', 'Search items', 'View cart', 'Order status']
    };

    setMessages([welcomeMessage]);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async (content: string) => {
    if (!content.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    // Check if user wants to list products
    const isProductListRequest = content.toLowerCase().includes('list') || 
                                content.toLowerCase().includes('show') || 
                                content.toLowerCase().includes('all products') ||
                                content.toLowerCase().includes('అన్ని ఉత్పత్తులు') ||
                                content.toLowerCase().includes('చూపించు') ||
                                content.toLowerCase().includes('జాబితా');

    // Check if user wants to stop product reading
    const isStopRequest = content.toLowerCase().includes('stop') || 
                         content.toLowerCase().includes('enough') || 
                         content.toLowerCase().includes('bas') ||
                         content.toLowerCase().includes('ఆపు') ||
                         content.toLowerCase().includes('సరిపోయింది') ||
                         content.toLowerCase().includes('అంతే');

    if (isStopRequest && isReadingProducts) {
      // Stop product reading
      setIsReadingProducts(false);
      isReadingProductsRef.current = false;
      setProductOffset(0);
      const stopMessage: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: currentLanguage === 'te' ? 
          "అర్థమైంది! ఉత్పత్తుల జాబితా చదవడం ఆపివేశాను. మీకు ఇంకా ఏమైనా సహాయం కావాలా?" :
          "Got it! I've stopped reading the product list. Is there anything else I can help you with?",
        timestamp: new Date(),
        audioUrl: `${process.env.REACT_APP_AI_SERVICE_URL}/api/v1/voice/text-to-speech?text=${encodeURIComponent(currentLanguage === 'te' ? 
          "అర్థమైంది! ఉత్పత్తుల జాబితా చదవడం ఆపివేశాను. మీకు ఇంకా ఏమైనా సహాయం కావాలా?" :
          "Got it! I've stopped reading the product list. Is there anything else I can help you with?")}&language=${currentLanguage}`
      };
      setMessages(prev => [...prev, stopMessage]);
      playAudio(stopMessage.audioUrl!);
      setIsLoading(false);
      return;
    }

    if (isProductListRequest) {
      // Start continuous product reading
      setIsReadingProducts(true);
      isReadingProductsRef.current = true;
      setProductOffset(0);
      setIsLoading(false);
      
      // Use setTimeout to ensure state is updated before starting
      setTimeout(() => {
        startContinuousProductReading();
      }, 100);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_AI_SERVICE_URL}/api/v1/conversation/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: content,
          user_id: userId,
          session_id: sessionId,
          language: currentLanguage,
          include_voice: true
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();
      
      const assistantMessage: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: data.response,
        timestamp: new Date(),
        audioUrl: data.audio_url,
        suggestions: data.suggestions,
        intent: data.intent,
        entities: data.entities
      };

      console.log('Assistant message with audio URL:', data.audio_url);
      setMessages(prev => [...prev, assistantMessage]);

      // Play audio if available (auto-play in voice mode)
      if (data.audio_url) {
        console.log('Auto-playing audio:', data.audio_url);
        if (voiceMode) {
          console.log('Voice mode is active, auto-playing response');
        }
        playAudio(data.audio_url);
      } else {
        console.log('No audio URL in response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const startListening = async () => {
    try {
      console.log('Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      console.log('Microphone access granted');
      
      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      // Clear previous audio chunks
      audioChunksRef.current = [];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let hasRecordedAudio = false;
      
      recorder.ondataavailable = (event) => {
        console.log('Audio data available:', event.data.size, 'bytes');
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          hasRecordedAudio = true;
          console.log('Total audio chunks:', audioChunksRef.current.length);
          
          // Reset silence timeout when we get audio data
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
          }
          
          // Set a timeout to stop recording after 2 seconds of silence
          silenceTimeoutRef.current = setTimeout(() => {
            console.log('Silence detected, stopping recording...');
            if (recorder && recorder.state === 'recording') {
              recorder.stop();
            }
          }, 2000); // 2 seconds of silence
        }
      };

      recorder.onstop = async () => {
        console.log('Recording stopped, processing audio...');
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        console.log('Audio blob size:', audioBlob.size, 'bytes');
        console.log('Audio chunks count:', audioChunksRef.current.length);
        
        // Clear timeouts
        if (recordingTimeoutRef.current) {
          clearTimeout(recordingTimeoutRef.current);
        }
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
        }
        
        if (audioBlob.size > 0) {
          setIsProcessingVoice(true);
          await processVoiceInput(audioBlob);
          setIsProcessingVoice(false);
        } else {
          console.warn('No audio data recorded');
          const noAudioMessage: Message = {
            id: Date.now().toString(),
            type: 'assistant',
            content: currentLanguage === 'te' ? 
              "ఆడియో రికార్డ్ కాలేదు. దయచేసి మళ్లీ మాట్లాడండి." :
              "No audio was recorded. Please try speaking again.",
            timestamp: new Date(),
            audioUrl: `${process.env.REACT_APP_AI_SERVICE_URL}/api/v1/voice/text-to-speech?text=${encodeURIComponent(currentLanguage === 'te' ? 
              "ఆడియో రికార్డ్ కాలేదు. దయచేసి మళ్లీ మాట్లాడండి." :
              "No audio was recorded. Please try speaking again.")}&language=${currentLanguage}`
          };
          setMessages(prev => [...prev, noAudioMessage]);
          playAudio(noAudioMessage.audioUrl!);
        }
        
        audioChunksRef.current = [];
        setIsListening(false);
      };

      recorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setIsListening(false);
        setIsProcessingVoice(false);
      };

      setMediaRecorder(recorder);
      recorder.start(100); // Collect data every 100ms
      setIsListening(true);
      console.log('Recording started');
      
      // Set a maximum recording time of 10 seconds
      recordingTimeoutRef.current = setTimeout(() => {
        console.log('Maximum recording time reached, stopping...');
        if (recorder && recorder.state === 'recording') {
          recorder.stop();
        }
      }, 10000); // 10 seconds max
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      const errorMessage: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: currentLanguage === 'te' ? 
          "మైక్రోఫోన్ అనుమతి తిరస్కరించబడింది. దయచేసి మైక్రోఫోన్ అనుమతులను ప్రారంభించండి." :
          "Microphone access denied. Please enable microphone permissions.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const stopListening = () => {
    if (mediaRecorder && isListening) {
      // Clear timeouts
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
      
      mediaRecorder.stop();
      setIsListening(false);
    }
  };

  const processVoiceInput = async (audioBlob: Blob) => {
    try {
      const data = await aiAssistantService.speechToText(audioBlob, currentLanguage);
      
      if (data.text) {
        await sendMessage(data.text);
      }
    } catch (error) {
      console.error('Error processing voice input:', error);
    }
  };

  const startContinuousProductReading = async () => {
    try {
      console.log('startContinuousProductReading called, isReadingProducts:', isReadingProducts);
      
      // First, send the initial response
      const initialMessage: Message = {
        id: Date.now().toString(),
        type: 'assistant',
        content: currentLanguage === 'te' ? 
          "అద్భుతం! నేను మీకు అన్ని ఉత్పత్తులను చూపిస్తాను! 🛍️ మీరు 'ఆపు' లేదా 'సరిపోయింది' అని చెప్పే వరకు నేను ఉత్పత్తులను చదవడం కొనసాగిస్తాను. ప్రారంభిద్దాం!" :
          "Awesome! I'll show you all our products! 🛍️ I'll keep reading them out until you say 'stop' or 'enough'. Let's get started!",
        timestamp: new Date(),
        audioUrl: `${process.env.REACT_APP_AI_SERVICE_URL}/api/v1/voice/text-to-speech?text=${encodeURIComponent(currentLanguage === 'te' ? 
          "అద్భుతం! నేను మీకు అన్ని ఉత్పత్తులను చూపిస్తాను! మీరు ఆపు లేదా సరిపోయింది అని చెప్పే వరకు నేను ఉత్పత్తులను చదవడం కొనసాగిస్తాను. ప్రారంభిద్దాం!" :
          "Awesome! I'll show you all our products! I'll keep reading them out until you say stop or enough. Let's get started!")}&language=${currentLanguage}`
      };
      
      setMessages(prev => [...prev, initialMessage]);
      playAudio(initialMessage.audioUrl!);
      
      // Wait for the initial message to finish playing, then start reading products
      setTimeout(() => {
        console.log('Timeout reached, calling readProductsChunk, isReadingProducts:', isReadingProducts);
        readProductsChunk();
      }, 3000); // Wait 3 seconds for initial message to finish
      
    } catch (error) {
      console.error('Error starting continuous product reading:', error);
    }
  };

  const readProductsChunk = async () => {
    console.log('readProductsChunk called, isReadingProducts:', isReadingProducts, 'ref:', isReadingProductsRef.current);
    if (!isReadingProducts && !isReadingProductsRef.current) {
      console.log('Not reading products, returning early');
      return;
    }
    
    try {
      console.log('Fetching products with offset:', productOffset);
      const response = await fetch(`${process.env.REACT_APP_AI_SERVICE_URL}/api/v1/conversation/products/continuous?language=${currentLanguage}&limit=3&offset=${productOffset}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      
      const data = await response.json();
      console.log('Products data received:', data);
      
      if (data.products && data.products.length > 0) {
        // Create product list text
        let productText = '';
        if (currentLanguage === 'te') {
          productText = `ఉత్పత్తి ${productOffset + 1} నుండి ${productOffset + data.products.length}: `;
          data.products.forEach((product: any, _index: number) => {
            productText += `${product.name}, ధర ${product.price} రూపాయలు. `;
          });
        } else {
          productText = `Products ${productOffset + 1} to ${productOffset + data.products.length}: `;
          data.products.forEach((product: any, _index: number) => {
            productText += `${product.name}, price ${product.price} rupees. `;
          });
        }
        
        const productMessage: Message = {
          id: Date.now().toString(),
          type: 'assistant',
          content: productText,
          timestamp: new Date(),
          audioUrl: `${process.env.REACT_APP_AI_SERVICE_URL}/api/v1/voice/text-to-speech?text=${encodeURIComponent(productText)}&language=${currentLanguage}`
        };
        
        setMessages(prev => [...prev, productMessage]);
        playAudio(productMessage.audioUrl!);
        
        // Update offset for next chunk
        setProductOffset(prev => prev + data.products.length);
        
        // If there are more products, schedule next chunk
        if (data.has_more && (isReadingProducts || isReadingProductsRef.current)) {
          setTimeout(() => {
            readProductsChunk();
          }, 5000); // Wait 5 seconds between chunks
        } else {
          // No more products, stop reading
          setIsReadingProducts(false);
          isReadingProductsRef.current = false;
          const endMessage: Message = {
            id: Date.now().toString(),
            type: 'assistant',
            content: currentLanguage === 'te' ? 
              "అన్ని ఉత్పత్తులు చదవడం పూర్తయింది! మీకు ఏదైనా ప్రత్యేక ఉత్పత్తి గురించి తెలుసుకోవాలనుకుంటున్నారా?" :
              "That's all the products! Would you like to know about any specific product?",
            timestamp: new Date(),
            audioUrl: `${process.env.REACT_APP_AI_SERVICE_URL}/api/v1/voice/text-to-speech?text=${encodeURIComponent(currentLanguage === 'te' ? 
              "అన్ని ఉత్పత్తులు చదవడం పూర్తయింది! మీకు ఏదైనా ప్రత్యేక ఉత్పత్తి గురించి తెలుసుకోవాలనుకుంటున్నారా?" :
              "That's all the products! Would you like to know about any specific product?")}&language=${currentLanguage}`
          };
          setMessages(prev => [...prev, endMessage]);
          playAudio(endMessage.audioUrl!);
        }
      } else {
        // No products found
        setIsReadingProducts(false);
        isReadingProductsRef.current = false;
      }
    } catch (error) {
      console.error('Error reading products chunk:', error);
      setIsReadingProducts(false);
      isReadingProductsRef.current = false;
    }
  };

  const playAudio = (audioUrl: string) => {
    // Use Web Speech API for better TTS
    if ('speechSynthesis' in window) {
      console.log('Using Web Speech API for TTS');
      
      // Extract text from the audio URL
      const urlParams = new URLSearchParams(audioUrl.split('?')[1]);
      const text = decodeURIComponent(urlParams.get('text') || '');
      
      if (text) {
        // Stop any current speech
        speechSynthesis.cancel();
        
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 0.8;
        
        // Set language for speech synthesis
        const languageMap: { [key: string]: string } = {
          'en': 'en-US',
          'es': 'es-ES',
          'hi': 'hi-IN',
          'te': 'te-IN',
          'fr': 'fr-FR',
          'de': 'de-DE',
          'zh': 'zh-CN'
        };
        utterance.lang = languageMap[currentLanguage] || 'en-US';
        
        utterance.onstart = () => {
          console.log('Speech started');
          setIsSpeaking(true);
        };
        
        utterance.onend = () => {
          console.log('Speech ended');
          setIsSpeaking(false);
        };
        
        utterance.onerror = (event) => {
          console.error('Speech error:', event);
          setIsSpeaking(false);
        };
        
        speechSynthesis.speak(utterance);
      } else {
        console.error('No text found in audio URL');
      }
    } else {
      // Fallback to audio element for beep sound
      console.log('Web Speech API not available, using audio element');
      
      if (audioRef.current) {
        console.log('Playing audio:', audioUrl);
        
        // Reset audio element
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = audioUrl;
        
        // Add error handling
        audioRef.current.onerror = (e) => {
          console.error('Audio playback error:', e);
          setIsSpeaking(false);
        };
        
        audioRef.current.onended = () => {
          console.log('Audio playback ended');
          setIsSpeaking(false);
        };
        
        // Load and play
        audioRef.current.load();
        audioRef.current.play()
          .then(() => {
            console.log('Audio started playing successfully');
            setIsSpeaking(true);
          })
          .catch((error) => {
            console.error('Failed to play audio:', error);
            setIsSpeaking(false);
          });
      }
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    sendMessage(suggestion);
  };

  const handleLanguageChange = (newLanguage: string) => {
    setCurrentLanguage(newLanguage);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputText);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`ai-assistant ${isMinimized ? 'minimized' : ''}`}>
      <div className="ai-assistant-header">
        <div className="ai-assistant-title">
          <div className="ai-assistant-avatar">🤖</div>
          <div>
            <h3>AI Assistant</h3>
            <span className="ai-assistant-status">Online</span>
          </div>
        </div>
        <div className="ai-assistant-controls">
          <select
            value={currentLanguage}
            onChange={(e) => handleLanguageChange(e.target.value)}
            className="language-selector"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="hi">हिन्दी</option>
            <option value="te">తెలుగు</option>
            <option value="fr">Français</option>
            <option value="de">Deutsch</option>
            <option value="zh">中文</option>
          </select>
          <button onClick={onMinimize} className="control-btn">
            <Minimize2 size={16} />
          </button>
          <button 
            onClick={() => {
              console.log('Voice mode button clicked, current state:', voiceMode);
              setVoiceMode(!voiceMode);
              console.log('Voice mode toggled to:', !voiceMode);
            }}
            className={`control-btn ${voiceMode ? 'active' : ''}`}
            title={voiceMode ? "Exit Voice Mode" : "Enter Voice Mode"}
          >
            {voiceMode ? '🎤' : '🎵'}
          </button>
          <button 
            onClick={() => {
              console.log('Test audio button clicked');
              // Test Web Speech API directly
              if ('speechSynthesis' in window) {
                const testTexts: { [key: string]: string } = {
                  'en': 'Hello! This is a test of the text to speech system.',
                  'es': '¡Hola! Esta es una prueba del sistema de texto a voz.',
                  'hi': 'नमस्ते! यह टेक्स्ट टू स्पीच सिस्टम का परीक्षण है।',
                  'te': 'హలో! ఇది టెక్స్ట్ టు స్పీచ్ సిస్టమ్ యొక్క పరీక్ష.',
                  'fr': 'Bonjour! Ceci est un test du système de synthèse vocale.',
                  'de': 'Hallo! Dies ist ein Test des Text-zu-Sprache-Systems.',
                  'zh': '你好！这是文本转语音系统的测试。'
                };
                
                const testText = testTexts[currentLanguage] || testTexts['en'];
                const utterance = new SpeechSynthesisUtterance(testText);
                utterance.rate = 0.9;
                utterance.pitch = 1;
                utterance.volume = 0.8;
                
                // Set language for speech synthesis
                const languageMap: { [key: string]: string } = {
                  'en': 'en-US',
                  'es': 'es-ES',
                  'hi': 'hi-IN',
                  'te': 'te-IN',
                  'fr': 'fr-FR',
                  'de': 'de-DE',
                  'zh': 'zh-CN'
                };
                utterance.lang = languageMap[currentLanguage] || 'en-US';
                
                speechSynthesis.speak(utterance);
              } else {
                playAudio(`${process.env.REACT_APP_AI_SERVICE_URL}/api/v1/voice/text-to-speech?text=Test&language=en`);
              }
            }} 
            className="control-btn"
            title="Test Audio"
          >
            🔊
          </button>
          <button onClick={onClose} className="control-btn">
            <X size={16} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
        {voiceMode && (
          <div className="voice-mode-indicator">
            🎤 Voice Mode Active - Click the microphone to start talking!
          </div>
        )}
        {isReadingProducts && (
          <div className="product-reading-indicator">
            📦 Reading Products - Say "stop" or "ఆపు" to stop
          </div>
        )}
        {isProcessingVoice && (
          <div className="voice-processing-indicator">
            🎤 Processing your voice... Please wait
          </div>
        )}
        <div className="ai-assistant-messages">
          {messages.map((message) => (
              <div key={message.id} className={`message ${message.type}`}>
                <div className="message-content">
                  <p>{message.content}</p>
                  {message.audioUrl && (
                    <button 
                      onClick={() => {
                        console.log('Speaker icon clicked, audio URL:', message.audioUrl);
                        playAudio(message.audioUrl!);
                      }}
                      className="play-audio-btn"
                      disabled={isSpeaking}
                    >
                      {isSpeaking ? <VolumeX size={16} /> : <Volume2 size={16} />}
                    </button>
                  )}
                </div>
                {message.suggestions && message.suggestions.length > 0 && (
                  <div className="suggestions">
                    {message.suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="suggestion-btn"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message assistant">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="ai-assistant-input">
            <div className="input-container">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message or use voice..."
                className="message-input"
                rows={1}
                disabled={isLoading}
              />
              <div className="input-controls">
                <button
                  onClick={isListening ? stopListening : startListening}
                  className={`voice-btn ${isListening ? 'listening' : ''}`}
                  disabled={isLoading}
                >
                  {isListening ? <MicOff size={20} /> : <Mic size={20} />}
                </button>
                <button
                  onClick={() => sendMessage(inputText)}
                  className="send-btn"
                  disabled={!inputText.trim() || isLoading}
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <audio ref={audioRef} />
    </div>
  );
};

export default AIAssistant;
